define(function (require, exports, module) {
    'use strict';

    const uploadRadar = require('./uploadRadar');
    var uploadLog = require('./uploadLog');
    var fileSystemEntryReader = require('./tools/fs/fileSystemEntryReader');
    var fileSystemPath = require('./tools/fs/fileSystemPath');
    var cloudFileSystem = require('./tools/cloud/cloudFileSystem');
    var Model = require('Model');
    var Uploader = require('./tools/cloud/Uploader');
    var UploaderData = require('./tools/cloud/UploaderData');
    var UploadReason = require('./UploadReason');
    var UploadCancel = require('./UploadCancel');
    var FileExistsFail = require('./fails/FileExistsFail');
    var HashCalcError = require('./fails/HashCalcError');
    var FileReaderError = require('./fails/FileReaderError');
    var FileTooLargeError = require('./errors/FileTooLargeError');
    var OverQuotaFail = require('./fails/OverQuotaFail');
    var ConnectionFail = require('./fails/ConnectionFail');
    var UserFileSizeLimitFail = require('./fails/UserFileSizeLimitFail');
    var JavaScriptError = require('./errors/JavaScriptError');
    var UnsupportedFolderTransferFail = require('./fails/UnsupportedFolderTransferFail');
    var LocalFileNotFoundFail = require('./fails/LocalFileNotFoundFail');
    var LocalFileNotReadableFail = require('./fails/LocalFileNotReadableFail');
    const { sizeGroups } = require('reactApp/constants/index');
    const { sendDwh } = require('utils/ga');
    const { path } = require('ramda');
    const { roundCustom } = require('reactApp/utils/helpers');
    const { getFileExtLimit } = require('reactApp/utils/textHelpers');

    const { updateUploadFilesAction, setProgressStatusAction } = require('reactApp/modules/uploadList/uploadList.module');
    const { EFileError, EFileStatus, EProgressStatus } = require('reactApp/modules/uploadList/uploadList.model');

    const { sendGaUploader } = require('reactApp/modules/uploadList/uploadList.helpers');
    const { store: reduxStore } = require('reactApp/store');

    var STATE_PENDING = 'pending';
    var STATE_UPLOADING = 'uploading';
    var STATE_UPLOADED = 'uploaded';
    var STATE_APPENDING = 'appending';
    var STATE_DONE = 'done';
    var STATE_FAIL = 'fail';
    var STATE_PAUSED = 'paused';
    var STATE_SUSPENDED = 'suspended';
    var STATE_CANCEL = 'cancel';

    /**
     * @constructor
     */
    var UploadDescriptor = Model.extend({
        /**
         * Название класса
         * @type {string}
         */
        className: 'UploadDescriptor',

        /**
         * Свойства по умолчанию
         * @type {Object}
         */
        defaults: {
            /**
             * Уникальный идентификатор.
             * @type {string}
             */
            id: '',

            /**
             * Имя файла или папки (в Unicode NFC) в локальной ФС.
             * @type {string}
             */
            localName: '',

            /**
             * Путь к файлу или папке (в Unicode NFC) в локальной ФС.
             * @type {string}
             */
            localPath: '',

            /**
             * Имя файла или папки (в Unicode NFC) в Облаке.
             * @type {string}
             */
            cloudName: '',

            /**
             * Путь к файлу или папке (в Unicode NFC) в Облаке.
             * @type {string}
             */
            cloudPath: '',

            /**
             * Имя файла или папки для отображения.
             * Пример: в macOS "file:name.ext" отображается, как "file/name.ext"
             * @type {string}
             */
            visibleName: '',

            /**
             * Путь к файлу или папке для отображения.
             * @type {string}
             */
            visiblePath: '',

            /**
             * Имя и расширение в формате для b-filename.
             * @type {Object}
             */
            nameParts: {
                /**
                 * Имя файла (без расширения) или папки.
                 * @type {string}
                 */
                name: '',

                /**
                 * Расширение файла в исходном регистре.
                 * @type {string}
                 */
                extension: '',
            },

            /**
             * Облачный hash файла (SHA1 + salt).
             * @type {string}
             */
            cloudHash: '',

            /**
             * Размер файла (в байтах).
             * @type {number}
             */
            size: -1,

            /**
             * Что-то вроде дескриптора файла или папки в локальной ФС,
             * содержат информацию о имени и относительном пути.
             * Служит для получения инстанса File.
             * @type {FileEntry|DirectoryEntry|null}
             */
            entry: null,

            /**
             * Соответствует файлу в локальной ФС.
             * Служит для получения содержимого файла.
             * @type {File}
             */
            file: null,

            /**
             * Любая ошибка.
             * @type {Error}
             */
            error: null,

            /**
             * @type {boolean}
             */
            isFile: false,

            /**
             * @type {boolean}
             */
            isDirectory: false,

            /**
             * Используется для рекурсивного чтения DirectoryEntry.
             * @type {boolean}
             */
            isUnreadDirectory: false,

            /**
             * Родительский каталог.
             * @type {UploadDescriptor|null}
             */
            parent: null,

            /**
             * Коллекция дескрипторов дочерних файлов и папок.
             * @type {Array}
             */
            children: [], // TODO: UploadDescriptor.List

            /**
             * @type {Uploader}
             */
            uploader: null,

            /**
             * Прогресс загрузки (в процентах).
             * @type {number}
             */
            progress: 0,

            /**
             * Состояние загрузки файла.
             * @type {number}
             */
            state: STATE_PENDING,

            /**
             * @type {Radar}
             */
            radar: null,

            conflictResolution: 'strict',

            uploaderData: null,

            publishStatus: 'inactive',
        },

        /**
         * @param {any} value
         * @returns {any} – new value
         */
        _validateError: function (value, descriptor) {
            var descriptor = this;
            var error = value;

            if (error) {
                if (!(error instanceof UploadReason)) {
                    var source = UploadReason.SOURCE_WEB_CLIENT;
                    var stack;

                    if (LocalFileNotFoundFail.isNotFoundError(error)) {
                        stack = new Error('LocalFileNotFoundFail');
                        error = new LocalFileNotFoundFail(stack, source);
                    } else if (descriptor.attributes.isDirectory && UnsupportedFolderTransferFail.isNotReadableError(error)) {
                        stack = new Error('UnsupportedFolderTransferFail');
                        error = new UnsupportedFolderTransferFail(stack, source);
                    } else if (LocalFileNotReadableFail.isNotReadableError(error)) {
                        stack = new Error('LocalFileNotReadableFail');
                        error = new LocalFileNotReadableFail(stack, source);
                    } else {
                        stack = new Error('JavaScriptError');
                        error = new JavaScriptError(stack, source, error);
                    }
                }

                error.setId(descriptor.get('id'));
            }

            return error;
        },

        _sendCounter: function () {
            var descriptor = this;
            var radar = descriptor.get('radar');

            if (!radar.alreadySended) {
                radar.send();
            }
        },

        /**
         * Используется для отправки счетчиков, если descriptor получил
         * конечное состояние до начала аплоада.
         * Ориентироваться на изменение атрибута state нельзя,
         * т.к. не известно, в каком порядке обновляются значения атрибутов.
         */
        finish: function () {
            this._sendCounter();
        },

        _logError: function (reason) {
            var radarName = reason.radarName;

            if (radarName) {
                // Дублируем причину ошибки рядом с состояниями,
                // размером файла и количеством попыток.
                radarName = radarName.replace(/^(fail|error|cancel)_/, '');
                radarName = radarName.replace(/_/g, '-');

                uploadRadar.sendCounter('reason-' + radarName);
            }

            uploadLog.reason(reason);
        },

        _logState: function (state) {
            var descriptor = this;
            var radar = descriptor.get('radar');

            radar.timeEnd('state-' + state);
            uploadLog.info({
                id: descriptor.get('id'),
                state: state,
            });
        },

        _logSize: function (size) {
            var descriptor = this;
            var radar = descriptor.get('radar');

            radar.add('size', size);
            uploadLog.info({
                id: descriptor.get('id'),
                size: size,
            });
        },

        /**
         * @param {any} value
         * @param {string} key
         * @param {Object} attributes
         * @param {UploadDescriptor} descriptor
         * @returns {any} – new value
         */
        valueValidator: function (value, key, attributes, descriptor) {
            var newValue = value;

            switch (key) {
                case 'error':
                    newValue = descriptor._validateError(value, descriptor);
                    if (newValue !== null) {
                        descriptor._logError(newValue);
                    }

                    break;

                case 'state':
                    descriptor._logState(newValue);

                    break;

                case 'size':
                    descriptor._logSize(newValue);

                    break;
            }

            return newValue;
        },

        /**
         * @param {UploaderData} data
         */
        _onRetry: function (data) {
            var descriptor = this;

            descriptor.set('progress', 0);

            var radar = descriptor.get('radar');
            var reason = data.error;

            if (reason) {
                reason = descriptor._validateError(reason);

                uploadLog.customizedReason(reason, 'retry');
            }

            radar.add('retry-' + data.attempt, 1);
        },

        /**
         * @param {UploaderData} data
         */
        _onProgress: function (data) {
            var descriptor = this;

            descriptor.set('progress', data.progress);

            reduxStore.dispatch(setProgressStatusAction({ status: EProgressStatus.PROGRESS }));
            reduxStore.dispatch(
                updateUploadFilesAction({
                    descriptorId: descriptor.get('id'),
                    cloudPath: descriptor.get('cloudPath'),
                    progress: data.progress,
                    loaded: data.loaded,
                    status: EFileStatus.PROGRESS,
                    error: '',
                })
            );
        },

        /**
         * @param {MicroEvent} event
         * @param {UploaderData} data
         */
        handleEvent: function (event, data) {
            switch (event.type) {
                case Uploader.EVENT_PROGRESS:
                    this._onProgress(data);

                    break;

                case Uploader.EVENT_RETRY:
                    this._onRetry(data);

                    break;
            }
        },

        /**
         * @returns {Promise}
         * @resolves {File}
         * @rejects {Error}
         */
        getFile: function () {
            var descriptor = this;
            var file = descriptor.get('file');
            var promise;

            if (file) {
                promise = Promise.resolve(file);
            } else {
                var entry = descriptor.get('entry');

                promise = fileSystemEntryReader.getFile(entry);

                promise = promise.then(function (file) {
                    descriptor.set({
                        file: file,
                        size: file.size,
                    });

                    return file;
                });

                promise = promise.catch(function (reason) {
                    descriptor.set({
                        error: reason,
                        state: STATE_FAIL,
                    });
                    descriptor._sendCounter();

                    return Promise.reject(reason);
                });
            }

            return promise;
        },

        failPaused: function () {
            let state = this.get('state');

            if (state === STATE_PAUSED) {
                this.set('state', STATE_FAIL);
            }
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploadReason|Error}
         */
        _upload: function () {
            var descriptor = this;

            var promise = Promise.resolve();
            var isUploaded = !!descriptor.get('cloudHash');

            if (!isUploaded) {
                promise = promise.then(function () {
                    return descriptor.getFile();
                });

                // Начинает загрузку.
                promise = promise.then(function () {
                    var uploader = new Uploader(descriptor);

                    uploader.on(Uploader.EVENT_PROGRESS, descriptor);
                    uploader.on(Uploader.EVENT_RETRY, descriptor);

                    descriptor.set('uploader', uploader);

                    var promise = uploader.upload();

                    return promise;
                });

                // Файл успешно загружен.
                promise = promise.then(function (data) {
                    var uploader = descriptor.get('uploader');

                    uploader.off(Uploader.EVENT_PROGRESS, descriptor);
                    uploader.off(Uploader.EVENT_RETRY, descriptor);

                    descriptor.set({
                        uploader: null,
                        progress: 100,
                        size: data.size,
                        cloudHash: data.hash,
                        state: STATE_UPLOADED,
                    });

                    uploadLog.info({
                        id: descriptor.get('id'),
                        hash: data.hash,
                        size: data.size,
                        speed: data.speed,
                        attempt: data.attempt,
                    });

                    var radar = descriptor.get('radar');

                    radar.add('speed', Math.round(data.speed));
                    radar.add('attempt', data.attempt);

                    descriptor.set('uploaderData', data);

                    return data;
                });
            } else {
                // пропустить шаг аплоада, если уже зааплоадили
                promise = promise.then(function () {
                    descriptor.set({
                        state: STATE_UPLOADED,
                    });

                    return descriptor.get('uploaderData');
                });
            }

            const clearUploader = () => {
                descriptor.set('uploader', false);
            };

            promise = promise.then((data) => {
                clearUploader();

                return data;
            });

            // Файл не загружен.
            promise = promise.catch(function (reason) {
                const uploader = descriptor.get('uploader');
                const cloudPath = descriptor.get('cloudPath');
                const descriptorId = descriptor.get('id');
                const wasFailRadarSent = descriptor.get('wasFailRadarSent');

                if (!wasFailRadarSent) {
                    descriptor.set('wasFailRadarSent', true);

                    const size = descriptor.get('size') || 0;
                    const isFile = descriptor.get('isFile');
                    let { extension = 'un' } = descriptor.get('nameParts');
                    if (!isFile) {
                        extension = 'folder';
                    }

                    const group = roundCustom(size, sizeGroups.values, sizeGroups.labels);
                    sendGaUploader(`errsize`, getFileExtLimit(extension), group);
                    sendDwh({
                        eventCategory: 'upload_error',
                        dwhData: {
                            extension,
                            sizeGroup: group,
                            size,
                            error: path(['error', 'radarName'], reason),
                        },
                    });
                }

                if (uploader) {
                    uploader.off(Uploader.EVENT_PROGRESS, descriptor);
                }

                var error;

                if (reason instanceof UploaderData) {
                    error = reason.error;
                } else {
                    error = reason;
                }

                var state;

                if (error instanceof LocalFileNotFoundFail) {
                    sendGaUploader('file', 'local_file_not_found');
                    reduxStore.dispatch(
                        updateUploadFilesAction({
                            descriptorId,
                            cloudPath,
                            currentUpload: false,
                            status: EFileStatus.ERROR,
                            error: EFileError.LOCAL_FILE_NOT_FOUND,
                        })
                    );
                } else if (error instanceof FileReaderError || error instanceof HashCalcError) {
                    reduxStore.dispatch(
                        updateUploadFilesAction({
                            descriptorId,
                            cloudPath,
                            currentUpload: false,
                            status: EFileStatus.ERROR,
                            error: EFileError.LOCAL_FILE_READ_ERROR,
                        })
                    );
                }

                if (error instanceof UserFileSizeLimitFail || error instanceof OverQuotaFail || error instanceof FileTooLargeError) {
                    state = STATE_PAUSED;
                } else if (error instanceof UploadCancel) {
                    state = STATE_CANCEL;
                } else if (error instanceof ConnectionFail) {
                    state = STATE_SUSPENDED;
                    sendGaUploader('suspended');
                } else {
                    state = STATE_FAIL;
                }

                var attributes = {
                    uploader: false,
                    progress: 0,
                    error: error,
                    state: state,
                    wasSuspended: state === STATE_SUSPENDED,
                };

                if (error instanceof UnsupportedFolderTransferFail) {
                    // Safari для папок создает инстанс File,
                    // но его никак нельзя использовать.
                    attributes.isFile = false;
                    attributes.isDirectory = true;
                    attributes.isUnreadDirectory = false;
                }

                descriptor.set(attributes);
                descriptor._sendCounter();

                return Promise.reject(error);
            });

            return promise;
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploadReason|Error}
         */
        upload: function () {
            var descriptor = this;
            var isDirectory = descriptor.get('isDirectory');
            var promise;

            if (isDirectory) {
                promise = Promise.reject(new Error('upload() called for directory'));
            } else {
                var state = descriptor.get('state');

                if (state === STATE_PENDING || state === STATE_PAUSED || state === UploadDescriptor.STATE_SUSPENDED) {
                    descriptor.set('state', STATE_UPLOADING);
                    promise = descriptor._upload();
                } else {
                    promise = Promise.reject(new Error('upload() called in wrong state'));
                }
            }

            return promise;
        },

        /**
         * @returns {Promise}
         * @resolves {string} – Путь к папке (в Unicode NFC) в Облаке.
         * @rejects {UploadReason|Error}
         */
        _addDirectoryToUserCloud: function () {
            var descriptor = this;
            var path = descriptor.get('cloudPath');
            var promise = cloudFileSystem.addFolder(path);

            return promise;
        },

        /**
         * @param workingDirectory
         * @returns {Promise}
         * @resolves {string} – Путь к файлу (в Unicode NFC) в Облаке.
         * @rejects {UploadReason|Error}
         */
        _addFileToUserCloud: function (workingDirectory = '') {
            var descriptor = this;
            var path = descriptor.get('cloudPath');
            var hash = descriptor.get('cloudHash');
            var size = descriptor.get('size');
            var descriptorId = descriptor.get('id');
            var conflictResolution = descriptor.get('conflictResolution');
            var localPath = descriptor.get('localPath');
            var isFolder = localPath && localPath.split('/').length > 2;

            var promise = cloudFileSystem.addFile(path, hash, size, conflictResolution, descriptorId, workingDirectory, isFolder);

            return promise;
        },

        /**
         * @param workingDirectory
         * @returns {Promise}
         * @resolves {string} – Путь к файлу или папке (в Unicode NFC) в Облаке.
         * @rejects {UploadReason|Error}
         */
        _addToUserCloud: function (workingDirectory = '') {
            const descriptor = this;
            const isDirectory = descriptor.get('isDirectory');
            var promise;

            if (isDirectory) {
                promise = descriptor._addDirectoryToUserCloud();
            } else {
                promise = descriptor._addFileToUserCloud(workingDirectory);
            }

            promise = promise.then(function (cloudPath) {
                const nameParts = fileSystemPath.getVisibleNameParts(cloudPath);
                const cloudName = fileSystemPath.getFullName(cloudPath);
                const attributes = {
                    cloudName,
                    cloudPath,
                    visibleName: fileSystemPath.getVisiblePath(cloudPath),
                    visiblePath: fileSystemPath.getVisiblePath(cloudPath),
                    nameParts,
                    state: STATE_DONE,
                };

                if (descriptor.get('hasInvalidCharAutoFix')) {
                    sendGaUploader('auto_fix', 'invalid_char_done');
                } else if (descriptor.get('hasNameTooLongAutoFix')) {
                    sendGaUploader('auto_fix', 'name_too_long_done');
                }

                descriptor.set(attributes);
                descriptor._sendCounter();

                reduxStore.dispatch(
                    updateUploadFilesAction({
                        descriptorId: descriptor.get('id'),
                        cloudPath,
                        currentUpload: false,
                        status: EFileStatus.DONE,
                        loaded: descriptor.get('size'),
                        progress: 100,
                        error: '',
                        name: nameParts ? nameParts.name : null, // имя может изменится после сохранения обоих файлов в случае file_exist
                    })
                );

                return cloudPath;
            });

            promise = promise.catch(function (reason) {
                let state;

                if (reason instanceof OverQuotaFail || reason instanceof UserFileSizeLimitFail || reason instanceof FileExistsFail) {
                    state = STATE_PAUSED;
                } else {
                    state = STATE_FAIL;
                }

                descriptor.set({
                    error: reason,
                    state: state,
                });

                descriptor._sendCounter();

                var promise = Promise.reject(reason);

                return promise;
            });

            return promise;
        },

        /**
         * @param workingDirectory
         * @returns {Promise}
         * @resolves {string} – Путь к файлу или папке (в Unicode NFC) в Облаке.
         * @rejects {UploadReason|Error}
         */
        addToUserCloud: function (workingDirectory = '') {
            var descriptor = this;
            var state = descriptor.get('state');
            var isDirectory = descriptor.get('isDirectory');
            var promise;

            if ((isDirectory && state === STATE_PENDING) || (!isDirectory && state === STATE_UPLOADED)) {
                descriptor.set('state', STATE_APPENDING);
                promise = descriptor._addToUserCloud(workingDirectory);
            } else {
                promise = Promise.reject(new Error('addToUserCloud() called in wrong state'));
            }

            return promise;
        },

        /**
         * @param {UploadCancel} reason
         */
        cancel: function (reason) {
            // TODO: cancel all descriptor.children
            var descriptor = this;
            var hasCancelableState = descriptor.hasCancelableState();

            if (hasCancelableState) {
                var uploader = descriptor.get('uploader');

                if (uploader) {
                    uploader.cancel();
                } else {
                    if (reason === undefined) {
                        var source = UploadReason.SOURCE_WEB_CLIENT;
                        var stack = new Error('UploadCancel');

                        reason = new UploadCancel(stack, source);
                    } else if (!(reason instanceof UploadCancel)) {
                        throw new TypeError('reason not cancel');
                    }

                    descriptor.set({
                        error: reason,
                        state: STATE_CANCEL,
                    });

                    if (!reason) {
                        // prevent this for cancel all
                        descriptor._sendCounter();
                    }
                }
            }
        },

        /**
         * @returns {boolean}
         */
        hasCancelableState: function () {
            var descriptor = this;
            var state = descriptor.get('state');
            var isCancelableState = UploadDescriptor.isCancelableState(state);

            return isCancelableState;
        },

        /**
         * @returns {boolean}
         */
        hasFinishedState: function () {
            var descriptor = this;
            var state = descriptor.get('state');
            var isFinishedState = UploadDescriptor.isFinishedState(state);

            return isFinishedState;
        },

        setConflictResolution: function (resolution) {
            if (!UploadDescriptor.isAllowedConflictResolution(resolution)) {
                resolution = 'strict';
            }

            this.set('conflictResolution', resolution);
        },
    });

    Object.assign(UploadDescriptor, {
        version: '0.0.1',

        STATE_PENDING: STATE_PENDING,
        STATE_SUSPENDED: STATE_SUSPENDED,
        STATE_UPLOADING: STATE_UPLOADING,
        STATE_UPLOADED: STATE_UPLOADED,
        STATE_APPENDING: STATE_APPENDING,
        STATE_DONE: STATE_DONE,
        STATE_FAIL: STATE_FAIL,
        STATE_PAUSED: STATE_PAUSED,
        STATE_CANCEL: STATE_CANCEL,

        cancelableStates: [STATE_PENDING, STATE_SUSPENDED, STATE_PAUSED, STATE_UPLOADING, STATE_UPLOADED],

        finishedStates: [STATE_DONE, STATE_FAIL, STATE_CANCEL],

        /**
         * @param {string} state
         * @returns {boolean}
         */
        isCancelableState: function (state) {
            var isCancelable = UploadDescriptor.cancelableStates.includes(state);

            return isCancelable;
        },

        /**
         * @param {string} state
         * @returns {boolean}
         */
        isFinishedState: function (state) {
            var isFinished = UploadDescriptor.finishedStates.includes(state);

            return isFinished;
        },

        _allowedConflictResolutions: ['strict', 'rewrite', 'rename'],

        isAllowedConflictResolution: function (resolution) {
            return this._allowedConflictResolutions.includes(resolution);
        },
    });

    module.exports = UploadDescriptor;
});
